import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { FolderOpen, Filter } from "lucide-react";

import narratorService from "@/services/narratorService";
import authService from "@/services/authService";
import NarratorHeader from "@/components/narrator/NarratorHeader";
import ProjectCard from "@/components/narrator/ProjectCard";

export default function NarratorProjects() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const profileData = await narratorService.getProfile().catch(() => null);
      if (!profileData) {
        toast.error("Please complete your profile first");
        navigate("/narrator/onboarding");
        return;
      }

      const projectsData = await narratorService.getProjects().catch(() => []);
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (error) {
      console.error("Error loading projects:", error);
      if (error.response?.status === 401) {
        authService.clearTokens();
        navigate("/login");
      } else if (error.response?.status === 403) {
        toast.error("Narrator role required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter((p) => {
    if (filter === "ALL") return true;
    if (filter === "ACTIVE")
      return !["COMPLETED", "QC_PASSED"].includes(p.status);
    if (filter === "COMPLETED")
      return ["COMPLETED", "QC_PASSED"].includes(p.status);
    return p.status === filter;
  });

  const filterOptions = [
    { value: "ALL", label: "All" },
    { value: "ACTIVE", label: "Active" },
    { value: "ASSIGNED", label: "Assigned" },
    { value: "RECORDING", label: "Recording" },
    { value: "SUBMITTED_FOR_QC", label: "In QC" },
    { value: "COMPLETED", label: "Completed" },
  ];

  // ✅ Loading State (with full background)
  if (loading) {
    return (
      <div className="relative min-h-screen">

        {/* FULL FIXED BACKGROUND */}
        <img
          src="/images/dashboard-bg.png"
          alt="Background"
          className="fixed inset-0 w-screen h-screen object-cover -z-10"
        />
        <div className="fixed inset-0 bg-black/10 -z-10"></div>

        <NarratorHeader />

        <div className="flex items-center justify-center h-[60vh] relative z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-800">Loading projects...</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ MAIN PAGE
  return (
    <div className="relative min-h-screen">

      {/* FULL FIXED BACKGROUND */}
      <img
        src="/images/dashboard-bg.png"
        alt="Background"
        className="fixed inset-0 w-screen h-screen object-cover -z-10"
      />
      <div className="fixed inset-0 bg-black/10 -z-10"></div>

      <NarratorHeader />

      <main className="container mx-auto px-6 py-10 relative z-10">

        {/* Header */}
        <div className="mb-10">
          <h1
            className="text-5xl font-bold mb-3 text-black"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            My Projects
          </h1>
          <p className="text-slate-800">
            View and manage your assigned book narration projects
          </p>
        </div>

        {/* Filters */}
        <Card className="bg-white/90 backdrop-blur-sm border-2 border-slate-200 mb-6">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-slate-600" />
              <div className="flex flex-wrap gap-2">
                {filterOptions.map((opt) => (
                  <Button
                    key={opt.value}
                    size="sm"
                    onClick={() => setFilter(opt.value)}
                    variant={filter === opt.value ? "default" : "outline"}
                    className={
                      filter === opt.value
                        ? "bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
                        : ""
                    }
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projects */}
        <div className="space-y-4">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() =>
                  navigate(`/narrator/projects/${project.id}`)
                }
              />
            ))
          ) : (
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-slate-200">
              <CardContent className="py-12 text-center">
                <FolderOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="text-lg font-semibold text-black mb-2">
                  {filter === "ALL"
                    ? "No projects yet"
                    : `No ${filter.toLowerCase()} projects`}
                </h3>
                <p className="text-slate-700">
                  {filter === "ALL"
                    ? "Complete your profile and demos to receive assignments"
                    : "Try a different filter to see more projects"}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Summary */}
        {projects.length > 0 && (
          <Card className="mt-8 bg-white/90 backdrop-blur-sm border-2 border-purple-200">
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-6 text-sm text-slate-800">
                <span>
                  Total: <strong>{projects.length}</strong>
                </span>
                <span>
                  Active:{" "}
                  <strong>
                    {
                      projects.filter(
                        (p) =>
                          !["COMPLETED", "QC_PASSED"].includes(p.status)
                      ).length
                    }
                  </strong>
                </span>
                <span>
                  Completed:{" "}
                  <strong>
                    {
                      projects.filter((p) =>
                        ["COMPLETED", "QC_PASSED"].includes(p.status)
                      ).length
                    }
                  </strong>
                </span>
                <span>
                  With QC Issues:{" "}
                  <strong className="text-red-600">
                    {
                      projects.filter((p) => p.open_qc_count > 0)
                        .length
                    }
                  </strong>
                </span>
              </div>
            </CardContent>
          </Card>
        )}

      </main>
    </div>
  );
}
